/**
 * fetchWithRetry - Lightweight fetch wrapper that retries on transient errors
 *
 * Retries on:
 * - HTTP 502 (Bad Gateway)
 * - HTTP 503 (Service Unavailable)
 * - Network errors (TypeError thrown by fetch)
 *
 * Does NOT retry on:
 * - 4xx errors (400, 401, 403, 404, 429, etc.)
 * - Successful responses (2xx)
 * - Other 5xx errors (500, 504) unless they are 502/503
 *
 * Uses exponential backoff: backoffMs * 2^attempt (e.g. 1000, 2000)
 */

const RETRYABLE_STATUSES = [502, 503];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// In-flight GET cache used by `dedupe: true`. Keyed by absolute URL.
// Multiple concurrent callers asking for the exact same URL (the
// pattern Chrome DevTools traced for /api/markets/kalshi/search ×3
// during a Polymarket → Kalshi switch) share one network round-trip;
// each caller gets its own `Response.clone()` so reading the body in
// one place doesn't lock out the others.
const inflightGets = new Map<string, Promise<Response>>();

async function doFetchWithRetry(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  maxRetries: number,
  backoffMs: number,
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(input, init);

      // Success or non-retryable status: return as-is
      if (!RETRYABLE_STATUSES.includes(response.status)) {
        return response;
      }

      // Retryable status but no more retries left: return the response
      if (attempt >= maxRetries) {
        return response;
      }

      // Wait with exponential backoff before retrying
      await sleep(backoffMs * Math.pow(2, attempt));
    } catch (error) {
      // Network errors (TypeError from fetch when offline, DNS failure, etc.)
      if (error instanceof TypeError) {
        lastError = error;

        // No more retries left: throw the original error
        if (attempt >= maxRetries) {
          throw error;
        }

        // Wait with exponential backoff before retrying
        await sleep(backoffMs * Math.pow(2, attempt));
      } else {
        // Non-transient error: throw immediately
        throw error;
      }
    }
  }

  // Should not be reached, but satisfy TypeScript
  throw lastError ?? new Error("fetchWithRetry: unexpected state");
}

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: { retries?: number; backoffMs?: number; dedupe?: boolean }
): Promise<Response> {
  const maxRetries = options?.retries ?? 2;
  const backoffMs = options?.backoffMs ?? 1000;

  // Opt-in dedup: only safe for idempotent GETs without body or
  // abort-signal. Any of those rule it out (POST etc. mutate state,
  // body bytes can't be replayed, abort would orphan other waiters).
  const isDedupable =
    options?.dedupe === true &&
    (init?.method ?? 'GET').toUpperCase() === 'GET' &&
    !init?.body &&
    !init?.signal;

  if (!isDedupable) {
    return doFetchWithRetry(input, init, maxRetries, backoffMs);
  }

  const key = typeof input === 'string' ? input : input.toString();
  const existing = inflightGets.get(key);
  if (existing) {
    return (await existing).clone();
  }

  const promise = doFetchWithRetry(input, init, maxRetries, backoffMs);
  inflightGets.set(key, promise);
  // Drop the entry once the request finishes (success or failure) —
  // microtask scheduling means concurrent waiters created during the
  // in-flight window have already grabbed the reference, so deleting
  // immediately on settle doesn't race them.
  promise.finally(() => {
    if (inflightGets.get(key) === promise) {
      inflightGets.delete(key);
    }
  });

  const response = await promise;
  return response.clone();
}
